import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { Loader2, Users } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  listUsuarios,
  reactivateUser,
  suspendUser,
  type AdminUser,
  type AdminUserListResponse,
  type DeletedFilter,
  type Membership,
  type SuspendedFilter,
} from '@/features/usuarios/api'

type PendingAction = { user: AdminUser; action: 'suspend' | 'reactivate' }

const DEBOUNCE_MS = 300
const PAGE_LIMIT = 50

const dateFmt = new Intl.DateTimeFormat('es-UY', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return dateFmt.format(d)
}

export function UsuariosListPage(): ReactNode {
  const queryClient = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [membership, setMembership] = useState<Membership>('all')
  const [deleted, setDeleted] = useState<DeletedFilter>('false')
  const [suspended, setSuspended] = useState<SuspendedFilter>('all')
  const [pending, setPending] = useState<PendingAction | null>(null)

  // Debounce manual (300ms) para no pegarle al endpoint en cada tecla.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput)
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [searchInput])

  const queryParams = useMemo(
    () => ({ search, membership, deleted, suspended, limit: PAGE_LIMIT }),
    [search, membership, deleted, suspended],
  )

  const suspendMutation = useMutation({
    mutationFn: ({ user, action }: PendingAction) =>
      action === 'suspend' ? suspendUser(user.id) : reactivateUser(user.id),
    onSuccess: (_res, vars) => {
      toast.success(
        vars.action === 'suspend' ? 'Usuario suspendido' : 'Usuario reactivado',
      )
      void queryClient.invalidateQueries({ queryKey: ['usuarios'] })
    },
    onError: (err: unknown, vars) => {
      const fallback =
        vars.action === 'suspend'
          ? 'No pudimos suspender al usuario'
          : 'No pudimos reactivar al usuario'
      toast.error(err instanceof Error && err.message ? err.message : fallback)
    },
    onSettled: () => setPending(null),
  })

  const fullName = (u: AdminUser): string =>
    `${u.nombre} ${u.apellido}`.trim() || u.email

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery<
    AdminUserListResponse,
    Error,
    { pages: AdminUserListResponse[]; pageParams: Array<string | null> },
    readonly unknown[],
    string | null
  >({
    queryKey: ['usuarios', 'list', queryParams] as const,
    queryFn: ({ pageParam }) =>
      listUsuarios({ ...queryParams, cursor: pageParam ?? undefined }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    placeholderData: keepPreviousData,
  })

  const items: AdminUser[] = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  )

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Usuarios</h1>
        <p className="text-sm text-muted-foreground">
          Listado de alumnos registrados. Podés suspender (baja reversible) las
          cuentas que incumplan las normas.
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label
            htmlFor="usuarios-search"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Buscar
          </label>
          <Input
            id="usuarios-search"
            placeholder="Nombre, apellido o email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <label
            htmlFor="usuarios-membership"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Membership
          </label>
          <Select
            id="usuarios-membership"
            value={membership}
            onChange={(e) => setMembership(e.target.value as Membership)}
          >
            <option value="all">Todas</option>
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <label
            htmlFor="usuarios-suspended"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Suspendidos
          </label>
          <Select
            id="usuarios-suspended"
            value={suspended}
            onChange={(e) => setSuspended(e.target.value as SuspendedFilter)}
          >
            <option value="all">Todos</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <label
            htmlFor="usuarios-deleted"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Eliminados
          </label>
          <Select
            id="usuarios-deleted"
            value={deleted}
            onChange={(e) => setDeleted(e.target.value as DeletedFilter)}
          >
            <option value="false">No</option>
            <option value="true">Sí</option>
            <option value="all">Todos</option>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead>Alta</TableHead>
              <TableHead>Proveedor</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Membership</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Seguidores</TableHead>
              <TableHead>Siguiendo</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((u) => (
              <UsuarioRow
                key={u.id}
                user={u}
                onAction={(action) => setPending({ user: u, action })}
              />
            ))}
          </TableBody>
        </Table>

        <div className="border-t p-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando...</p>
          ) : isError ? (
            <p className="text-sm text-destructive">
              No pudimos cargar los usuarios
              {error?.message ? `: ${error.message}` : '.'}
            </p>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Users className="h-8 w-8" />}
              title="Aún no hay usuarios registrados"
              description="Cuando los alumnos se registren desde la app, aparecerán en este listado."
            />
          ) : (
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                {items.length} {items.length === 1 ? 'usuario' : 'usuarios'}
                {isFetching && !isFetchingNextPage ? ' (actualizando...)' : ''}
              </p>
              {hasNextPage ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void fetchNextPage()
                  }}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? 'Cargando...' : 'Cargar mas'}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.action === 'suspend'
                ? 'Suspender usuario'
                : 'Reactivar usuario'}
            </DialogTitle>
            <DialogDescription>
              {pending
                ? pending.action === 'suspend'
                  ? `Vas a suspender a ${fullName(pending.user)}. Perderá el acceso a la app de inmediato hasta que lo reactives.`
                  : `Vas a reactivar a ${fullName(pending.user)}. Recuperará el acceso a la app.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPending(null)}
              disabled={suspendMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant={pending?.action === 'suspend' ? 'destructive' : 'default'}
              disabled={suspendMutation.isPending}
              onClick={() => {
                if (pending) suspendMutation.mutate(pending)
              }}
            >
              {suspendMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {pending?.action === 'suspend' ? 'Suspender' : 'Reactivar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function UsuarioRow({
  user,
  onAction,
}: {
  user: AdminUser
  onAction: (action: 'suspend' | 'reactivate') => void
}): ReactNode {
  const isDeleted = user.deletedAt != null
  const isSuspended = user.suspendedAt != null
  const isAdmin = user.rol === 'ADMIN'
  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <span className={isDeleted ? 'text-muted-foreground line-through' : ''}>
            {user.nombre} {user.apellido}
          </span>
          {isDeleted ? <Badge variant="destructive">Eliminado</Badge> : null}
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">{user.email}</TableCell>
      <TableCell className="text-muted-foreground">
        {user.telefono || '-'}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDate(user.createdAt)}
      </TableCell>
      <TableCell>
        <ProviderBadge provider={user.authProvider} />
      </TableCell>
      <TableCell>
        {user.rol === 'ADMIN' ? (
          <Badge variant="default">Admin</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">Alumno</span>
        )}
      </TableCell>
      <TableCell>
        <MembershipBadge activeUntil={user.activeUntil} />
      </TableCell>
      <TableCell>
        {isSuspended ? (
          <Badge variant="warning">Suspendido</Badge>
        ) : (
          <Badge variant="success">Activo</Badge>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground tabular-nums">{user.followersCount}</TableCell>
      <TableCell className="text-muted-foreground tabular-nums">{user.followingCount}</TableCell>
      <TableCell className="text-right">
        {isAdmin ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : isSuspended ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction('reactivate')}
          >
            Reactivar
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction('suspend')}
          >
            Suspender
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}

function ProviderBadge({
  provider,
}: {
  provider: AdminUser['authProvider']
}): ReactNode {
  switch (provider) {
    case 'GOOGLE':
      return <Badge variant="secondary">Google</Badge>
    case 'APPLE':
      return <Badge variant="muted">Apple</Badge>
    case 'PASSWORD':
      return <Badge variant="outline">Password</Badge>
    default:
      return <Badge variant="outline">{provider}</Badge>
  }
}

function MembershipBadge({
  activeUntil,
}: {
  activeUntil: string | null
}): ReactNode {
  if (!activeUntil) {
    return <Badge variant="outline">Sin activar</Badge>
  }
  const until = new Date(activeUntil)
  if (Number.isNaN(until.getTime())) {
    return <Badge variant="outline">Sin activar</Badge>
  }
  if (until.getTime() > Date.now()) {
    return <Badge variant="success">Activa hasta {formatDate(activeUntil)}</Badge>
  }
  return <Badge variant="muted">Vencida</Badge>
}
